import React, { useState, useMemo, useRef, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAuth } from '../context/AuthContext';
import { useRequests } from '../context/RequestsContext';
import { useToast, toast } from '../components/UI/Toast';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import { ApiService, ClientStatus } from '../services/api';
import { authService } from '../services/authService';
import { 
  Shield, Copy, Share2, Check, ChevronDown, X, Send,
  FileText, RefreshCcw, Users, CheckCircle2, Clock, UserMinus
} from 'lucide-react';

// Animation keyframes
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// (removed) slideInLeft animation was only used by stat cards


const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
  animation: ${fadeInUp} 0.6s ease-out;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const HeaderSection = styled.div`
  margin-bottom: 2.5rem;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const WelcomeGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const Avatar = styled.div<{ theme: any }>`
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, #973031 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.25rem;
  font-weight: 700;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
`;

const WelcomeText = styled.div`
  flex: 1;
`;

const WelcomeTitle = styled.h1<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 1.75rem;
  font-weight: 800;
  margin: 0 0 0.25rem 0;
  letter-spacing: -0.02em;
`;

const WelcomeSubtitle = styled.p<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.95rem;
  margin: 0;
  font-weight: 500;
`;

const IdSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-left: 2rem;
  
  @media (max-width: 768px) {
    margin-left: 0;
    margin-top: 1rem;
  }
`;

const IdBadgeInline = styled.div<{ theme: any }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: ${props => props.theme.colors.background};
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  letter-spacing: 0.5px;
`;


const IdDropdown = styled.div`
  position: relative;
`;

const IdDropdownButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop)
})<{ theme: any }>`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem;
  border-radius: 6px;
  border: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.textSecondary};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.text};
  }
`;

const IdDropdownMenu = styled.div.withConfig({
  shouldForwardProp: (prop) => !['theme', 'isOpen'].includes(prop)
})<{ theme: any; isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  min-width: 120px;
  padding: 0.5rem 0;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: translateY(${props => props.isOpen ? '0' : '-10px'});
  transition: all 0.2s ease-out;
  z-index: 120;
`;

const IdDropdownItem = styled.button.withConfig({
  shouldForwardProp: (prop) => !['theme', 'copied'].includes(prop)
})<{ theme: any; copied?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: none;
  background: ${props => props.copied ? props.theme.colors.success + '15' : 'transparent'};
  color: ${props => props.copied ? props.theme.colors.success : props.theme.colors.text};
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.copied ? props.theme.colors.success + '20' : props.theme.colors.background};
  }
`;

const ReferenceCard = styled(Card)`
  padding: 1.25rem 1.25rem;
  margin-top: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const ReferenceLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  min-width: 260px;
  flex: 1;
`;

const ReferenceIcon = styled.div<{ theme: any }>`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${p => p.theme.colors.primary}14;
  color: ${p => p.theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
`;

const ReferenceText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
`;

const ReferenceTitle = styled.div<{ theme: any }>`
  color: ${p => p.theme.colors.text};
  font-weight: 900;
  font-size: 1rem;
  letter-spacing: -0.01em;
`;

const ReferenceSub = styled.div<{ theme: any }>`
  color: ${p => p.theme.colors.textSecondary};
  font-weight: 600;
  font-size: 0.9rem;
  line-height: 1.35;
`;

const ReferenceCodeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 0.25rem;
`;

const ReferenceCode = styled.div<{ theme: any }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: ${p => p.theme.colors.background};
  border: 2px solid ${p => p.theme.colors.border};
  border-radius: 10px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.9rem;
  font-weight: 700;
  color: ${p => p.theme.colors.primary};
  letter-spacing: 0.6px;
`;

const ReferenceActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 0 0 auto;
`;



const PendingCard = styled(Card)`
  padding: 1.25rem 1.25rem;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const PendingLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  min-width: 260px;
  flex: 1;
`;

const PendingIcon = styled.div<{ theme: any }>`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${p => p.theme.colors.primary}14;
  color: ${p => p.theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
`;

const PendingText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
`;

const PendingTitle = styled.div<{ theme: any }>`
  color: ${p => p.theme.colors.text};
  font-weight: 900;
  font-size: 1rem;
  letter-spacing: -0.01em;
`;

const PendingSub = styled.div<{ theme: any }>`
  color: ${p => p.theme.colors.textSecondary};
  font-weight: 600;
  font-size: 0.9rem;
  line-height: 1.35;
`;

const PendingCount = styled.div<{ theme: any }>`
  display: inline-flex;
  align-items: baseline;
  gap: 0.5rem;
  margin-top: 0.25rem;
  color: ${p => p.theme.colors.text};
  font-weight: 950;
  font-size: 1.6rem;
`;

const PendingCountLabel = styled.span<{ theme: any }>`
  color: ${p => p.theme.colors.textSecondary};
  font-weight: 800;
  font-size: 0.9rem;
`;

const PendingActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 0 0 auto;
`;

const ClientStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;
  margin-top: 1rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const ClientStatCard = styled(Card)`
  padding: 1.1rem 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.9rem;
`;

const ClientStatIcon = styled.div<{ theme: any; $variant: 'primary' | 'success' | 'warning' | 'muted' }>`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;

  background: ${p =>
    p.$variant === 'success'
      ? `${p.theme.colors.success}14`
      : p.$variant === 'warning'
        ? `#f59e0b14`
        : p.$variant === 'muted'
          ? `${p.theme.colors.border}40`
          : `${p.theme.colors.primary}14`};

  color: ${p =>
    p.$variant === 'success'
      ? p.theme.colors.success
      : p.$variant === 'warning'
        ? '#f59e0b'
        : p.$variant === 'muted'
          ? p.theme.colors.textSecondary
          : p.theme.colors.primary};
`;

const ClientStatText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
`;

const ClientStatValue = styled.div<{ theme: any }>`
  color: ${p => p.theme.colors.text};
  font-weight: 950;
  font-size: 1.4rem;
  line-height: 1.1;
`;

const ClientStatLabel = styled.div<{ theme: any }>`
  color: ${p => p.theme.colors.textSecondary};
  font-weight: 800;
  font-size: 0.85rem;
  letter-spacing: 0.2px;
`;

// Share Modal Components
const ModalOverlay = styled.div.withConfig({
  shouldForwardProp: (prop) => !['theme', 'isOpen'].includes(prop)
})<{ theme: any; isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
`;

const ModalContent = styled.div.withConfig({
  shouldForwardProp: (prop) => !['theme', 'isOpen'].includes(prop)
})<{ theme: any; isOpen: boolean }>`
  background: ${props => props.theme.colors.surface};
  border-radius: 16px;
  padding: 2.5rem;
  width: 90%;
  max-width: 650px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  transform: ${props => props.isOpen ? 'scale(1) translateY(0)' : 'scale(0.9) translateY(20px)'};
  transition: all 0.3s ease;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const CloseButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop)
})<{ theme: any }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: none;
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.colors.border};
    color: ${props => props.theme.colors.text};
  }
`;

const ShareForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FormLabel = styled.label<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 0.875rem;
  font-weight: 600;
`;

const FormInput = styled.input.withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop)
})<{ theme: any }>`
  width: 100%;
  padding: 0.875rem 1rem;
  border-radius: 10px;
  border: 2px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: 0.95rem;
  outline: none;
  transition: all 0.2s ease;
  
  &:focus {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const FormTextarea = styled.textarea.withConfig({
  shouldForwardProp: (prop) => !['theme'].includes(prop)
})<{ theme: any }>`
  width: 100%;
  padding: 1rem 1.25rem;
  border-radius: 10px;
  border: 2px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: 0.95rem;
  outline: none;
  transition: all 0.2s ease;
  resize: vertical;
  min-height: 150px;
  font-family: inherit;
  line-height: 1.6;
  
  &:focus {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const IdPreview = styled.div<{ theme: any }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  background: ${props => props.theme.colors.background};
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: 12px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 1.1rem;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  letter-spacing: 0.8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const DashboardPage: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { user, consultant, currentUser } = useAuth();
  const { pendingRequestsCount, refreshPendingRequestsCount } = useRequests();
  const { showToast } = useToast();
  const [clientStats, setClientStats] = useState<{
    total: number;
    active: number;
    pending: number;
    disengaged: number;
  } | null>(null);
  const [isLoadingClientStats, setIsLoadingClientStats] = useState(false);
  const [copiedId, setCopiedId] = useState(false);
  const [idDropdownOpen, setIdDropdownOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareForm, setShareForm] = useState({
    recipientName: '',
    recipientEmail: '',
    message: ''
  });
  const dropdownRef = useRef<HTMLDivElement>(null);

  usePageTitle('Dashboard');

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIdDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const consultantTaxId = useMemo(() => {
    const profile: any = user?.profile || {};
    const raw = consultant?.taxId || profile?.taxId || profile?.taxid || profile?.taxID;
    return typeof raw === 'string' && raw.trim() ? raw.trim() : '—';
  }, [consultant?.taxId, user?.profile]);

  const isOwnerRole = useMemo(() => {
    const role = typeof currentUser?.role === 'string' ? currentUser.role.trim().toUpperCase() : '';
    return role === 'OWNER';
  }, [currentUser?.role]);

  const loadClientStats = async () => {
    const token = authService.getAccessToken();
    if (!token) {
      setClientStats(null);
      return;
    }

    setIsLoadingClientStats(true);
    try {
      const base = { page: 1, pageSize: 1 } as const;
      const [all, active, inactive, archived] = await Promise.all([
        ApiService.listClients(token, { ...base }),
        ApiService.listClients(token, { ...base, status: 'ACTIVE' as ClientStatus }),
        ApiService.listClients(token, { ...base, status: 'INACTIVE' as ClientStatus }),
        ApiService.listClients(token, { ...base, status: 'ARCHIVED' as ClientStatus }),
      ]);

      setClientStats({
        total: all.pagination?.total ?? 0,
        active: active.pagination?.total ?? 0,
        pending: inactive.pagination?.total ?? 0,
        disengaged: archived.pagination?.total ?? 0,
      });
    } catch {
      // keep prior stats if refresh fails
    } finally {
      setIsLoadingClientStats(false);
    }
  };

  useEffect(() => {
    void loadClientStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const givenName = (user?.profile as any)?.given_name as string | undefined;
  const familyName = (user?.profile as any)?.family_name as string | undefined;
  const welcomeName =
    (typeof givenName === 'string' && givenName.trim())
      ? givenName.trim()
      : (user?.profile?.given_name || user?.profile?.name || 'Tax Consultant');
  const avatarInitials = getInitials(
    [givenName, familyName].filter(Boolean).join(' ') || (user?.profile?.name || user?.profile?.email || 'TC'),
  );

  const handleCopyId = async () => {
    try {
      await navigator.clipboard.writeText(consultantTaxId);
      setCopiedId(true);
      setIdDropdownOpen(false);
      showToast(toast.success('Copied!', 'Tax ID copied to clipboard'));
      setTimeout(() => setCopiedId(false), 2000);
    } catch (error) {
      showToast(toast.error('Failed', 'Could not copy to clipboard'));
    }
  };

  const handleShareId = () => {
    setIdDropdownOpen(false);
    setShareModalOpen(true);
    setShareForm({
      recipientName: '',
      recipientEmail: '',
      message: `Hi there!\n\nI'd like to share my Tax ID with you: ${consultantTaxId}\n\nYou can connect with me on the NRS Tax Consultant Portal for professional tax consulting services including tax assessments, filing management, payment processing, and compliance tracking.\n\nBest regards,\n${consultant?.firmName || user?.profile?.name || 'Tax Consultant'}`
    });
  };

  const handleCloseModal = () => {
    setShareModalOpen(false);
    setShareForm({
      recipientName: '',
      recipientEmail: '',
      message: ''
    });
  };

  const handleFormChange = (field: string, value: string) => {
    setShareForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleShareSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!shareForm.recipientEmail.trim()) {
      showToast(toast.error('Required Field', 'Please enter recipient email address'));
      return;
    }

    try {
      // Simulate sending email (in real app, this would call an API)
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      showToast(toast.success('Shared Successfully!', `Consultant code shared with ${shareForm.recipientEmail}`));
      handleCloseModal();
    } catch (error) {
      showToast(toast.error('Share Failed', 'Could not send the share message. Please try again.'));
    }
  };

  if (!user) {
    return (
      <Container>
        <Card>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <h2>Access Denied</h2>
            <p>You need to be logged in to access the dashboard.</p>
          </div>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <HeaderSection>
        <TopRow>
          <WelcomeGroup>
            <Avatar theme={theme}>
              {avatarInitials}
            </Avatar>
            <WelcomeText>
              <WelcomeTitle theme={theme}>
                Welcome back, {welcomeName}!
              </WelcomeTitle>
              <WelcomeSubtitle theme={theme}>
                Here's your {(consultant?.firmName || 'tax consulting')} dashboard overview
              </WelcomeSubtitle>
            </WelcomeText>
          </WelcomeGroup>
          <IdSection>
            <div>
              <IdBadgeInline theme={theme}>
                <Shield size={12} />
                {consultantTaxId}
              </IdBadgeInline>
            </div>
            <IdDropdown ref={dropdownRef}>
              <IdDropdownButton 
                theme={theme} 
                onClick={() => setIdDropdownOpen(!idDropdownOpen)}
              >
                <ChevronDown size={14} />
              </IdDropdownButton>
              <IdDropdownMenu theme={theme} isOpen={idDropdownOpen}>
                <IdDropdownItem 
                  theme={theme} 
                  copied={copiedId} 
                  onClick={handleCopyId}
                >
                  {copiedId ? <Check size={12} /> : <Copy size={12} />}
                  {copiedId ? 'Copied!' : 'Copy Tax ID'}
                </IdDropdownItem>
                <IdDropdownItem 
                  theme={theme} 
                  onClick={handleShareId}
                >
                  <Share2 size={12} />
                  Share
                </IdDropdownItem>
              </IdDropdownMenu>
            </IdDropdown>
          </IdSection>
        </TopRow>

        <ReferenceCard>
          <ReferenceLeft>
            <ReferenceIcon theme={theme}>
              <Shield size={20} />
            </ReferenceIcon>
            <ReferenceText>
              <ReferenceTitle theme={theme}>Tax ID Reference</ReferenceTitle>
              <ReferenceSub theme={theme}>
                Use this tax ID to reference your practice when engaging clients or NRS support.
              </ReferenceSub>
              <ReferenceCodeRow>
                <ReferenceCode theme={theme}>
                  <Shield size={14} />
                  {consultantTaxId}
                </ReferenceCode>
              </ReferenceCodeRow>
            </ReferenceText>
          </ReferenceLeft>

          <ReferenceActions>
            <Button variant="outline" size="small" onClick={handleCopyId}>
              {copiedId ? <Check size={16} /> : <Copy size={16} />}
              {copiedId ? 'Copied' : 'Copy'}
            </Button>
            <Button variant="outline" size="small" onClick={handleShareId}>
              <Share2 size={16} />
              Share
            </Button>
          </ReferenceActions>
        </ReferenceCard>

        <ClientStatsGrid>
          <ClientStatCard>
            <ClientStatIcon theme={theme} $variant="primary">
              <Users size={20} />
            </ClientStatIcon>
            <ClientStatText>
              <ClientStatValue theme={theme}>
                {isLoadingClientStats ? '—' : String(clientStats?.total ?? 0)}
              </ClientStatValue>
              <ClientStatLabel theme={theme}>Total Clients</ClientStatLabel>
            </ClientStatText>
          </ClientStatCard>

          <ClientStatCard>
            <ClientStatIcon theme={theme} $variant="success">
              <CheckCircle2 size={20} />
            </ClientStatIcon>
            <ClientStatText>
              <ClientStatValue theme={theme}>
                {isLoadingClientStats ? '—' : String(clientStats?.active ?? 0)}
              </ClientStatValue>
              <ClientStatLabel theme={theme}>Active Clients</ClientStatLabel>
            </ClientStatText>
          </ClientStatCard>

          <ClientStatCard>
            <ClientStatIcon theme={theme} $variant="warning">
              <Clock size={20} />
            </ClientStatIcon>
            <ClientStatText>
              <ClientStatValue theme={theme}>
                {isLoadingClientStats ? '—' : String(clientStats?.pending ?? 0)}
              </ClientStatValue>
              <ClientStatLabel theme={theme}>Pending Clients</ClientStatLabel>
            </ClientStatText>
          </ClientStatCard>

          <ClientStatCard>
            <ClientStatIcon theme={theme} $variant="muted">
              <UserMinus size={20} />
            </ClientStatIcon>
            <ClientStatText>
              <ClientStatValue theme={theme}>
                {isLoadingClientStats ? '—' : String(clientStats?.disengaged ?? 0)}
              </ClientStatValue>
              <ClientStatLabel theme={theme}>Disengaged Clients</ClientStatLabel>
            </ClientStatText>
          </ClientStatCard>
        </ClientStatsGrid>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
          <Button
            variant="outline"
            size="small"
            onClick={async () => {
              await loadClientStats();
              showToast(toast.success('Updated', 'Client stats refreshed.'));
            }}
            disabled={isLoadingClientStats}
          >
            <RefreshCcw size={16} />
            Refresh client stats
          </Button>
        </div>

        <PendingCard>
          <PendingLeft>
            <PendingIcon theme={theme}>
              <FileText size={20} />
            </PendingIcon>
            <PendingText>
              <PendingTitle theme={theme}>Pending Requests</PendingTitle>
              <PendingSub theme={theme}>
                {isOwnerRole ? 'Requests awaiting your review and approval.' : 'Only Owners can view and manage requests.'}
              </PendingSub>
              <PendingCount theme={theme}>
                {isOwnerRole ? pendingRequestsCount : 0}
                <PendingCountLabel theme={theme}>pending</PendingCountLabel>
              </PendingCount>
            </PendingText>
          </PendingLeft>

          <PendingActions>
            <Button
              variant="outline"
              size="small"
              disabled={!isOwnerRole}
              onClick={async () => {
                try {
                  await refreshPendingRequestsCount();
                  showToast(toast.success('Updated', 'Pending requests refreshed.'));
                } catch {
                  showToast(toast.error('Failed', 'Could not refresh pending requests.'));
                }
              }}
            >
              <RefreshCcw size={16} />
              Refresh
            </Button>
            <Button
              size="small"
              disabled={!isOwnerRole}
              onClick={() => navigate('/requests')}
            >
              View Requests
            </Button>
          </PendingActions>
        </PendingCard>
      </HeaderSection>

      {/* Share Modal */}
      <ModalOverlay theme={theme} isOpen={shareModalOpen} onClick={handleCloseModal}>
        <ModalContent 
          theme={theme} 
          isOpen={shareModalOpen} 
          onClick={(e) => e.stopPropagation()}
        >
          <ModalHeader>
            <ModalTitle theme={theme}>
              <Share2 size={24} />
              Share Tax ID
            </ModalTitle>
            <CloseButton theme={theme} onClick={handleCloseModal}>
              <X size={20} />
            </CloseButton>
          </ModalHeader>

          <ShareForm onSubmit={handleShareSubmit}>
                <FormGroup>
                  <FormLabel theme={theme}>Your Tax ID</FormLabel>
                  <IdPreview theme={theme}>
                    <Shield size={20} />
                    {consultantTaxId}
                  </IdPreview>
                </FormGroup>

            <FormGroup>
              <FormLabel theme={theme}>Recipient Name (Optional)</FormLabel>
              <FormInput
                theme={theme}
                type="text"
                placeholder="Enter recipient's name"
                value={shareForm.recipientName}
                onChange={(e) => handleFormChange('recipientName', e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <FormLabel theme={theme}>Recipient Email *</FormLabel>
              <FormInput
                theme={theme}
                type="email"
                placeholder="Enter recipient's email address"
                value={shareForm.recipientEmail}
                onChange={(e) => handleFormChange('recipientEmail', e.target.value)}
                required
              />
            </FormGroup>

            <FormGroup>
              <FormLabel theme={theme}>Message</FormLabel>
              <FormTextarea
                theme={theme}
                placeholder="Add a personal message..."
                value={shareForm.message}
                onChange={(e) => handleFormChange('message', e.target.value)}
              />
            </FormGroup>

            <ModalActions>
              <Button variant="outline" type="button" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button type="submit">
                <Send size={16} style={{ marginRight: '0.5rem' }} />
                Send Share
              </Button>
            </ModalActions>
          </ShareForm>
        </ModalContent>
      </ModalOverlay>
    </Container>
  );
};

export default DashboardPage;
